import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { apis } from 'service';
import { COMMON_URL_PRESFOR, BASE_URL_PRESFOR } from 'service/api';
import { Notification, useToaster } from 'rsuite';
import secureStorage from 'hooks/secureStorage';

const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const toaster = useToaster();

    // Ref pour le debounce des toasts
    const toastTimeoutRef = useRef(null);
    const pendingToastsRef = useRef([]);

    useEffect(() => {
        const token = secureStorage.getItem('usertoken_session');
        if (!token) return;

        const socket = io(BASE_URL_PRESFOR);

        fetchNotifications();

        socket.on('connect', () => {
            console.log('Connected to notification WebSocket');
        });

        socket.on('nouvelle_notification', (data) => {
            if (data.type === 'read') {
                fetchNotifications();
                return;
            }
            pendingToastsRef.current.push(data);

            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }

            toastTimeoutRef.current = setTimeout(() => {
                showGroupedToast();
            }, 500);

            fetchNotifications();
        });

        return () => {
            socket.disconnect();
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);

    const fetchNotifications = async () => {
        const token = secureStorage.getItem('usertoken_session');
        if (!token) return;

        try {
            const response = await apis.get(`${COMMON_URL_PRESFOR}notifications`);
            if (response && response.data) {
                // Filtrer pour n'afficher que les rappels (Globaux ou Régionaux)
                const filtered = response.data.filter(n =>
                    n.targetId === 'GLOBAL_DELAY' ||
                    n.targetId === 'CONTRAT_SOUMIS' ||
                    (n.targetId && n.targetId.startsWith('REGION_DELAY_'))
                );
                setNotifications(filtered);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    const showGroupedToast = () => {
        const count = pendingToastsRef.current.length;
        if (count === 0) return;

        const message = count === 1
            ? `Une nouvelle notification de rappel.`
            : `Vous avez reçu ${count} notifications de rappel.`;

        toaster.push(
            <Notification type="info" header="Relances" closable>
                {message}
            </Notification>,
            { placement: 'topEnd', duration: 5000 }
        );

        // Reset
        pendingToastsRef.current = [];
    };

    const markAsRead = async (id) => {
        try {
            await apis.patch(`${COMMON_URL_PRESFOR}notifications/${id}/read`);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, isRead: true } : n)
            );
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        const unreadNotifs = notifications.filter(n => !n.isRead);
        if (unreadNotifs.length === 0) return;

        try {
            await Promise.all(unreadNotifs.map(n =>
                apis.patch(`${COMMON_URL_PRESFOR}notifications/${n.id}/read`)
            ));
            setNotifications(prev =>
                prev.map(n => ({ ...n, isRead: true }))
            );
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
        }
    };

    const unreadCount = notifications.filter(n => !n.isRead).length;

    return (
        <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications }}>
            {children}
        </NotificationContext.Provider>
    );
};
